import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Inbox, FileText } from 'lucide-react';
import { FranchiseeHeader } from './FranchiseeHeader';
import { listMyAdoptions } from '../api/adopt';
import { formatDateTime } from '../lib/format';

const STATUS_STYLE: Record<string, string> = {
  Accepted: 'bg-status-success-soft text-status-success',
  Declined: 'bg-status-danger-soft text-status-danger',
  InProgress: 'bg-surface-muted text-graphite-500',
};

/** A franchisee's past responses + links to their printable agreements (PRD §10). */
export function MyAdoptionsPage() {
  const { data, isLoading } = useQuery({ queryKey: ['my-adoptions'], queryFn: listMyAdoptions });

  return (
    <div className="min-h-screen bg-surface-canvas">
      <FranchiseeHeader />
      <main className="mx-auto max-w-3xl px-4 py-8">
        <h1 className="font-display text-2xl font-bold tracking-tight text-byte-black">My adoptions</h1>
        <p className="mt-1 text-sm text-graphite-500">Your product adoption decisions and their signed agreements.</p>
        {isLoading ? (
          <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-byte-purple" /></div>
        ) : !data || data.length === 0 ? (
          <div data-testid="my-adoptions-empty" className="mt-6 flex flex-col items-center rounded-2xl border border-dashed border-border-strong bg-white px-6 py-12 text-center">
            <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-warm text-byte-purple ring-1 ring-byte-purple/15">
              <Inbox className="h-6 w-6" />
            </span>
            <p className="text-sm font-semibold text-graphite-800">No adoptions yet</p>
            <p className="mt-1 max-w-sm text-sm text-graphite-500">When your Byte representative shares an adoption link, your products will appear here to review and accept.</p>
          </div>
        ) : (
          <ul data-testid="my-adoptions" className="mt-6 flex flex-col gap-2.5">
            {data.map((r) => (
              <li key={`${r.linkId}-${r.seq}`} className="flex items-center justify-between gap-3 rounded-xl border border-border-subtle bg-white px-4 py-3.5 shadow-xs transition hover:border-byte-purple/30 hover:shadow-sm">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-graphite-900">{(r.selectedProductNames ?? r.selectedProducts).join(', ') || '—'}</p>
                  <p className="mt-0.5 text-xs text-graphite-400">Updated {formatDateTime(r.updatedAt)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLE[r.status] ?? 'bg-surface-muted text-graphite-500'}`}>
                    <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" aria-hidden="true" />
                    {r.status}
                  </span>
                  {r.status === 'Accepted' && (
                    <Link to={`/agreement/${r.linkId}/${r.seq}`} className="inline-flex items-center gap-1 rounded-md text-xs font-medium text-status-info transition hover:underline">
                      <FileText className="h-3.5 w-3.5" /> View agreement
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
